import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { Box, Button, FormControlLabel, Grid, Radio, RadioGroup, Switch } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { LanguageContext } from '../App';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { deleteVisualizacaoNovidade, getNovidade, getVisualizacoesNovidade, insertNovidade, updateNovidade } from '../apis/apiNovidade';
import InputCustom from '../components/InputCustom';
import DataPickerCustom from '../components/RangeDataPicker';
import dayjs from 'dayjs';
import SelectCustom from '../components/SelectCustom';
import TextEditor from '../components/HtmlTextEditor';
import { RichTextEditorRef } from 'mui-tiptap/dist/cjs/RichTextEditor';
import NovidadeImage from '../assets/imgs/novidade.png';
import AvisoImage from '../assets/imgs/aviso.png';
import Table from '../components/Table';
import fieldsTables from '../utils/fieldsTables';
import showColumns from '../utils/showColumns';

function Novidade() {
  const [loading, setLoading] = useState<boolean>(true)
  const [searchParams] = useSearchParams();
  const { translations, setLanguage } = useContext(LanguageContext);
  const [novidade, setNovidade] = useState<Novidade>();
  const [usuarios, setUsuarios] = useState<UsuarioNovidade[]>([])
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<UsuarioNovidade[]>([])
  const [buscaUsuario, setBuscaUsuario] = useState("");
  const [selectedAcao, setSelectedAcao] = useState<string>("");
  const [ hasModified, setHasModified ] = useState<boolean>(false);
  const rteRefPt = useRef<RichTextEditorRef>(null);
  const rteRefEs = useRef<RichTextEditorRef>(null);
  const [idiomaVisualizacao, setIdiomaVisualizacao] = useState<string>("pt");

  useEffect(() => {
    setLoading(true)
    const languageParam = searchParams.get('language') || 'pt'
    const acao = searchParams.get('acao') || "novo"
    setLanguage(languageParam)
    setLoading(false)
    setSelectedAcao(acao)
  }, [searchParams, setLanguage]);

  const handleNovidadeLink = (link: string) => {
    if (link && link.length) {
      if (link.includes("web/adm")) {
        const splitLink = link.split("adm");
        return `../adm${splitLink[1]}`
      } else {
        return link
      }
    } else {
      return link
    }
  }

  const handleSaveNovidade = useCallback(async (ativar: boolean) => {
    
    if (novidade) {
      const token = searchParams.get('token')
      const language = searchParams.get('language') || 'pt'
      const idNovidade = searchParams.get('id_novidade') || "";

      const mappedData = {
        ...novidade,
        ativo: ativar,
        minDate: dayjs(novidade.minDate).format('DD/MM/YYYY'),
        maxDate: dayjs(novidade.maxDate).format('DD/MM/YYYY')
      }

      let result: any = "";

      switch (selectedAcao) {
        case "novo":
          result = await insertNovidade(mappedData, token)
          break;
        case "editar":
          result = hasModified ? await updateNovidade(mappedData, token, idNovidade) : { status: 200 }
          break;
        case "copiar":
          result = await insertNovidade(mappedData, token)
          break;
        case "visualizar":
          result = { status: 200 }
          break;
        default:
          break;
      }

      if (result.status === 201 || result.status === 200) {
        window.location.href = `../listar/novidades?token=${token}&language=${language}`
      }
    }  

  }, [hasModified, novidade, searchParams, selectedAcao])

  const handleGetNovidade = useCallback(async () => {

    const idNovidade = searchParams.get('id_novidade') || "";

    if (idNovidade.length > 0) {

      const token = searchParams.get('token')

      const result = await getNovidade(token, idNovidade);

      if (selectedAcao === "copiar") {        
        const mappedData = {
          ...result.data,
          ativo: false,
          tituloPt: `${result.data.tituloPt} - ${translations["lbl.react.protocolo.copia"]}`
        }
        setNovidade(mappedData);
      } else {
        setNovidade(result.data);
      }

    }
    
  }, [searchParams, selectedAcao, translations]);

  const handleGetVisualizacoesNovidade = useCallback(async () => {

    const idNovidade = searchParams.get('id_novidade') || "";

    if (!["copiar", "novo"].includes(selectedAcao) && idNovidade.length > 0) {

      const token = searchParams.get('token')

      const result = await getVisualizacoesNovidade(token, idNovidade);

      if (result.status === 200) {
        setUsuarios(result.data)
        setUsuariosFiltrados(result.data)
      }

    }
    
  }, [searchParams, selectedAcao]);

  const handleApis = useCallback(async () => {
    setLoading(true);
    try {
     
      if (["editar", "copiar", "visualizar"].includes(selectedAcao)) {
        await handleGetNovidade();
        await handleGetVisualizacoesNovidade();
      } else {
        const novaNovidade: Novidade = {
          tituloPt: '',
          tituloEn: '',
          tituloEs: '',
          descricaoPt: '',
          descricaoEn: '',
          descricaoEs: '',
          link: '',
          tipo: 'novidade',
          ambiente: 'portal',
          minDate: '',
          maxDate: '',
          urgente: false,
          ativo: false
        }
        setNovidade(novaNovidade);
      }
    } catch (error) {
      console.error("Erro ao realizar as requisições:", error);
    } finally {
      setLoading(false);
    }
  }, [handleGetNovidade, handleGetVisualizacoesNovidade, selectedAcao])

  useEffect(() => {
    handleApis()
  }, [handleApis]);

  const handleNovidadeInputChange = (event: any) => {
    const {
      target: { value, name },
    } = event;

    if (name === "link") {
      setNovidade(prevState => (
        { ...prevState!, ...{link: handleNovidadeLink(value)} })
      )
    } else {
      setNovidade(prevState => (
        { ...prevState!, ...{[name]: value} })
      )
    }


    setHasModified(true)   
  };

  const handleTextInputChangePt = () => {

    setNovidade(prevState => (
      { ...prevState!, ...{descricaoPt: rteRefPt.current!.editor!.getHTML()} })
    )
 
  };

  const handleBuscaUsuario = (event: any) => {
    const {
      target: { value },
    } = event;

    setBuscaUsuario(value)
    if (value.length > 0) {
      const valueSplit = value.toString().toUpperCase().split(" ");
      setUsuariosFiltrados(usuarios.filter((usuario: Usuario) => (
        usuario.usuCodigo.includes(value.toString()) ||
        valueSplit.every((split: string) => usuario.usuNome.includes(split)) ||
        (usuario.usuLogin.toUpperCase()).includes(value.toString().toUpperCase()) ||
        (usuario.usuEmail.toUpperCase()).includes((value.toString().toUpperCase())
      ))))
    } else {
      setUsuariosFiltrados(usuarios)
    }
  };

  const handleTextInputChangeEs = () => {

    setNovidade(prevState => (
      { ...prevState!, ...{descricaoEs: rteRefEs.current!.editor!.getHTML()} })
    )
 
  };

  const handleProtocoloSwitchChange = () => {
    setNovidade(prevState => (
      { ...prevState!, ativo: !prevState!.ativo })
    )

    setHasModified(true)   
  };

  const handleRadioChange = (event: any) => {
        const {
      target: { value },
    } = event;
    setIdiomaVisualizacao(value)
  };

  const datePickerHandleChange = ([newStartDate, newEndDate]: [newStartDate: Date, newEndDate: Date]) => {
    setNovidade(prevState => (
      {...prevState!, ...{ minDate: newStartDate, maxDate: newEndDate }}
    ))
  };

  const datePickerHandleClear = () => {
    setNovidade(prevState => (
      { ...prevState!, minDate: "", maxDate: "" }))
  };

  const handleDeleteVisualizacao = async (idVisualizacao: number) => {

    if (idVisualizacao) {

      const token = searchParams.get('token')

      const result = await deleteVisualizacaoNovidade(token, idVisualizacao.toString());

      if (result.status === 200) {
        setUsuarios(usuarios.filter(usuario => usuario.idVisualizacao !== result.data))
        setUsuariosFiltrados(usuariosFiltrados.filter(usuario => usuario.idVisualizacao !== result.data))
      }
    }
  }

  const handlePageTitle = () => {
    const acao = searchParams.get('acao') || "novo"
    switch (acao) {
      case "novo":
        return "Cadastrar"
      case "editar":
        return "Editar"
      case "copiar":
        return "Cadastrar"
      case "visualizar":
        return "Visualizar"
      default:
        return "Cadastrar"
    }
  }

  return (
    <Grid container justifyContent="center">
      { translations &&
        <Grid item xs={12}>
          <div className="page-body">
            <span className="page-title">
              Novidade / Aviso
            </span>
            { loading === false ?
              <Grid container rowSpacing={2} justifyContent="center">
                <Grid item xs={12} container display={"flex"} justifyContent={"space-between"}>
                  <Grid container padding="22px" pr={0} mb={1} item xs={12}>
                    <Grid item xs={6} mb={5}>
                      <span onClick={() => window.history.back()} className="page-bread pointer">
                        Novidades/Avisos
                      </span>
                      <span className="page-bread">
                        { ` > ${handlePageTitle()}` }
                      </span>
                    </Grid>
                    <Grid item xs={6} textAlign={"end"} flexDirection={"row"}>
                      <Button onClick={() => window.history.back()} className="btn-secundario btn-voltar" variant="contained"><ArrowBackIcon />Voltar para lista de Novidades/Avisos</Button>
                    </Grid>
                    <Grid item xs={12} className="page-title">
                      <span>
                      { handlePageTitle() }
                      </span>
                      <span className={`badge-status ${novidade!.ativo ? "active" : "inactive"}`} style={{marginLeft: "16px", padding: "10px"}}>
                        { novidade!.ativo ? "Publicado" : "Bloqueado"}
                      </span>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{backgroundColor: "#f7f7f7", border: "1px solid #f7f7f7", borderRadius: "3px", marginRight: "8px"} }>
                      <Grid container padding="22px">
                        <Grid item container xs={12} mt={4} columnSpacing={2}>
                          <Grid item xs={6}>
                            <span className="input-label">Tipo</span>
                            <SelectCustom
                                placeholder={"Selecione o tipo:"}
                                handleChange={handleNovidadeInputChange}
                                items={[
                                  {value: "Aviso", valueKey: "aviso"},
                                  {value: "Novidade", valueKey: "novidade"}
                                ]}
                                selectedItem={novidade!.tipo}
                                name="tipo"
                                isValid={true}
                                readOnly={selectedAcao === "visualizar" ? true : false}
                              />
                          </Grid>
                          <Grid item xs={6}>
                            <span className="input-label">Ambiente</span>
                            <SelectCustom
                                placeholder={"Selecione o ambiente:"}
                                handleChange={handleNovidadeInputChange}
                                items={[
                                  {value: "Portal", valueKey: "portal"},
                                  {value: "App", valueKey: "app"},
                                  {value: "Portal e App", valueKey: "portal_app"}
                                ]}
                                selectedItem={novidade!.ambiente}
                                name="ambiente"
                                isValid={true}
                                readOnly={selectedAcao === "visualizar" ? true : false}
                              />
                          </Grid>
                          <Grid item xs={12}>
                            <span className="input-label">Título em Português</span>
                            <InputCustom
                              placeholder={''}
                              controlledValue={novidade!.tituloPt}
                              name={'tituloPt'}
                              handleChange={handleNovidadeInputChange}
                              isValid={true}
                              readOnly={selectedAcao === "visualizar" ? true : false}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <span className="input-label">Título em Espanhol</span>
                            <InputCustom
                              placeholder={''}
                              controlledValue={novidade!.tituloEs}
                              name={'tituloEs'}
                              handleChange={handleNovidadeInputChange}
                              isValid={true}
                              readOnly={selectedAcao === "visualizar" ? true : false}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <span className="input-label">Link</span>
                            <InputCustom
                              placeholder={''}
                              controlledValue={novidade!.link}
                              name={'link'}
                              handleChange={handleNovidadeInputChange}
                              isValid={true}
                              readOnly={selectedAcao === "visualizar" ? true : false}
                            />
                          </Grid>
                        </Grid>
                        <Grid item container xs={12} mt={1}>
                          <Grid item xs={6}>
                            <span className="input-label">Período de Exibição</span>
                            <DataPickerCustom
                              onlyMonths={false}
                              dateFormat="dd/MM/yyyy"
                              handleChange={datePickerHandleChange}
                              handleClear={datePickerHandleClear}
                              handleClickOutside={() => {}}
                              startDate={novidade!.minDate ? new Date(novidade!.minDate) : null}
                              endDate={novidade!.maxDate ? new Date(novidade!.maxDate) : null}
                              selectRange={true}
                              placeholder=""
                              limitMaxDate={false}
                              isValid={true}
                              readOnly={selectedAcao === "visualizar" ? true : false}
                            />  
                          </Grid>
                        </Grid>
                        <Grid item xs={12} mt={1} zIndex={1}>
                          <span className="input-label">Descrição em Português</span>
                          <TextEditor
                            ref={rteRefPt}
                            content={novidade!.descricaoPt}
                            handleChange={handleTextInputChangePt}
                            editable={selectedAcao === "visualizar" ? false : true}
                          />
                        </Grid>
                        <Grid item xs={12} mt={1} zIndex={1}>
                          <span className="input-label">Descrição em Espanhol</span>
                          <TextEditor
                            ref={rteRefEs}
                            content={novidade!.descricaoEs}
                            handleChange={handleTextInputChangeEs}
                            editable={selectedAcao === "visualizar" ? false : true}
                          />
                        </Grid>
                        <Grid item xs={12} mt={1}>
                          <Switch
                            className="switch-protocolo"
                            checked={novidade!.urgente}
                            onChange={handleProtocoloSwitchChange}
                            inputProps={{ 'aria-label': 'controlled' }}
                            name='urgente'
                            disabled={selectedAcao === "visualizar" ? true : false}
                          />
                          <span className="input-label">Urgente</span>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{height: "100%", backgroundColor: "#f7f7f7", border: "1px solid #f7f7f7", borderRadius: "3px"} }>
                      <Grid container justifyContent={"center"} alignItems={"center"}>
                        <Grid item xs={12} container justifyContent={"center"} mt={8} mb={10}>
                          <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            onChange={handleRadioChange}
                            value={idiomaVisualizacao}
                          >
                            <FormControlLabel value="pt" control={<Radio />} label="Português" />
                            <FormControlLabel value="es" control={<Radio />} label="Espanhol" />
                          </RadioGroup>
                        </Grid>
                        <div style={{width: "500px", backgroundColor: "#fff", border: "1px #d1d4d6 solid", borderRadius: "16px"}}>
                          <div style={{padding: "20px 32px", fontFamily: 'roboto, sans-serif'}}>
                            <Grid item xs={12} textAlign={"center"} mb={"16px"}>
                              <strong className="tituloNovidade">{idiomaVisualizacao === "pt" ? novidade?.tituloPt : novidade?.tituloEs}</strong>
                            </Grid>
                            <Grid item xs={12} textAlign={"center"}>
                              { novidade?.tipo === "novidade" ?
                                <img className="imagemNovidade" src={NovidadeImage} alt="news" />
                                :
                                <img className="imagemNovidade" src={AvisoImage} alt="warning" />
                              }
                            </Grid>
                            <div className="ambienteBox">
                              { novidade!.ambiente === "portal" ?
                                  <div className="ambientePortal">Portal</div>
                                : novidade!.ambiente === "app" ?
                                  <div className="ambienteApp">App</div>
                                :
                                  <div style={{display: "flex", columnGap: "10px"}}>
                                    <div className="ambientePortal">Portal</div>
                                    <div className="ambienteApp">App</div>
                                  </div>
                                }
                              <div style={{fontSize: "14px", color: "#607d8b"}}>{dayjs(novidade!.minDate).format("DD/MM/YYYY")}</div>
                            </div>
                            <Grid item xs={12} className="descriacaoContainer" mt={1}>
                                <span style={{display: idiomaVisualizacao === "pt" ? "block" : "none"}} className="descricaoNovidade" dangerouslySetInnerHTML={{__html: novidade!.descricaoPt}}></span>
                                <span style={{display: idiomaVisualizacao === "es" ? "block" : "none"}} className="descricaoNovidade" dangerouslySetInnerHTML={{__html: novidade!.descricaoEs}}></span>
                                <span style={{display: idiomaVisualizacao === "en" ? "block" : "none"}} className="descricaoNovidade" dangerouslySetInnerHTML={{__html: novidade!.descricaoEn}}></span>
                            </Grid>
                            <div className="botao-container">
                              { novidade?.link && !novidade?.link.includes("http") ?
                                <button className="botaoNovidade">{idiomaVisualizacao === "pt" ? "Veja agora" : "Vea ahora"}</button>
                              : novidade?.link && novidade?.link.includes("http") ?
                                <button className="botaoNovidade">{idiomaVisualizacao === "pt" ? "Veja agora" : "Vea ahora"}</button>
                              :
                                <button className="btn-primario">{idiomaVisualizacao === "pt" ? "Entendi" : "Entendí"}</button>
                              }       	
                            </div>                               
                          </div>
                        </div>                    
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid mt={2} mb={3} container justifyContent="end" alignItems="center" columnGap={2}>
                    { ["novo", "copiar", "editar"].includes(selectedAcao) &&
                      <Grid>
                        <Button onClick={() => window.history.back()} className="btn-secundario" variant="contained" >{ translations["lbl.react.cancelar"] }</Button>
                      </Grid>
                    }
                    <Grid>
                      <Button onClick={() => handleSaveNovidade(novidade!.ativo)} className="btn-primario" variant="contained" >{ ["novo", "copiar", "editar"].includes(selectedAcao) ? "Salvar" : "OK" }</Button>
                    </Grid>
                    { (["novo", "copiar"].includes(selectedAcao) && !novidade?.ativo) &&
                      <Grid>
                        <Button onClick={() => handleSaveNovidade(true)} className="btn-primario" variant="contained" >Salvar e Publicar</Button>
                      </Grid>
                    }
                  </Grid>
                </Grid>
                { !["copiar", "novo"].includes(selectedAcao) &&
                  <Grid item xs={12}>
                    <Box sx={{ width: "100%", backgroundColor: "#f7f7f7", border: "1px solid #f7f7f7", borderRadius: "3px"} }>
                      <Grid container item xs={12} justifyContent={"end"}>
                        <Grid item xs={4}  p={2} mt={2}>
                          <span className="input-label">{`${translations["lbl.react.buscar"]}:`}</span>
                          <InputCustom
                            placeholder={''}
                            controlledValue={buscaUsuario}
                            name={'buscaUsuario'}
                            handleChange={handleBuscaUsuario}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} p={2} mb={3}>
                        <Table
                          columns={fieldsTables(translations, handleDeleteVisualizacao, null).UsuariosNovidade}
                          rows={usuariosFiltrados}
                          idColumn='idVisualizacao'
                          sortColumn='idVisualizacao'
                          sortOrder="desc"
                          hasCheckbox={false}
                          tableTitle={"Visualizações"}
                          tableSubTitle={""}
                          name="usuarios"
                          printButton={false}
                          showColumns={showColumns.usuariosNovidade}
                          handleCloseTable={() => {}}
                          border={true}
                        />
                      </Grid>
                      </Box>
                  </Grid>
                }
              </Grid>
              :
              <Loading setHeight="85vh"/>
            }
          </div>
        </Grid>
      }
    </Grid>
  );
}

export default Novidade;